import React, { Fragment } from "react";
import { Layout, Layouts, Responsive, WidthProvider } from 'react-grid-layout';
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { debounce } from "throttle-debounce";
import { appDomain } from '../../domain/AppDomain';
import { EventNames } from "../../domain/events/Events";
import { IEmployeeGroupAbsences } from "../../domain/employeeGroups/IEmployeeGroupAbsences";
import { IRoom } from "../../domain/calendars/IRoom";
import { dateFormat } from "../../helpers/dateHelpers";
import { BaseComponent } from "../core/BaseComponent";
import { AbsenceDayTable } from "../day/AbsenceDayTable";
import "./AttentionDaysGridLayout.css";
import { ICalendar } from "../../domain/calendars/ICalendar";

const ResponsiveGridLayout = WidthProvider(Responsive);

interface AttentionDaysGridLayoutProps {
    date: Date;
    showPresentAndAbsencesInGroups: boolean;
    onLoading: (isLoading: boolean, isCurrentlyInMeeting: boolean, lastSynctAt?: Date) => void;
}

interface AttentionDaysGridLayoutState {
    groupedEmployees: IEmployeeGroupAbsences[],
    isChanged: boolean,
    updatedAt: Date;
    currentDate: Date;
    layout: any;
    rooms: Array<IRoom>;
}

const localstorageKey = "rgl-7-v1";
export class AttentionDaysGridLayout extends BaseComponent<AttentionDaysGridLayoutProps, AttentionDaysGridLayoutState> {
    private _timeout: number = 0;
    private _layoutBreakpoints = ["lg", "md", "sm", "xs", "xxs"];

    constructor(props: AttentionDaysGridLayoutProps) {
        super(props);
        this.state = {} as AttentionDaysGridLayoutState;
    }
    componentWillUnmount() {
        window.clearTimeout(this._timeout);
    }

    private loadWithDebounce: Function = debounce(500, () => this.loadData());

    async loadData(args?: { changed: boolean }) {
        if (!this.mounted()) return;

        const isChanged = !!args?.changed;

        window.clearTimeout(this._timeout);
        this._timeout = window.setTimeout(this.loadData.bind(this), 20000);

        this.props.onLoading(true, false, undefined);
        try {
            if (!this.props.showPresentAndAbsencesInGroups) {

                if (!this.state.groupedEmployees) {
                    this.setStateWhenMounted({ groupedEmployees: await appDomain.IEmployeeService.getEmployeeGroupAbsences() });
                }

                const employeeAbsences = await appDomain.IEmployeeAbsenceService.getAbsenceForDay(this.props.date, isChanged);
                const groupedEmployees = this.state.groupedEmployees;
                await appDomain.IEmployeeAbsenceService.fillAbsencesOnGroupedEmployees(groupedEmployees, employeeAbsences);

                this.fixLayoutIfErrorInLocalstorage(groupedEmployees);


                this.props.onLoading(false, employeeAbsences.currentlyInMeeting, employeeAbsences.exchangeSyncedAt);
                this.setStateWhenMounted({ updatedAt: new Date(), groupedEmployees });
            } else {
                const employeeGroupAbsences = await appDomain.IEmployeeService.getAbsencesForAbsentPresentOverview();
                this.setStateWhenMounted({ updatedAt: new Date(), groupedEmployees: employeeGroupAbsences });
            }
        } finally {
            this.props.onLoading(false, false, undefined);
        }
    }
    fixLayoutIfErrorInLocalstorage(groupedEmployees: IEmployeeGroupAbsences[]) {
        const allLayouts = JSON.parse(JSON.stringify(this.state.layout));

        if (allLayouts) {
            this._layoutBreakpoints.forEach(key => {
                const currentLayout = allLayouts[key];
                if (currentLayout) {
                    groupedEmployees.forEach((group, index) => {
                        const calculatedSize = this.getSize(group.employees, group, index, groupedEmployees.length, this.props.showPresentAndAbsencesInGroups)
                        const foundGroup = currentLayout.find((x: any) => x.i === group.id);
                        if (foundGroup && (foundGroup.h !== calculatedSize.h || foundGroup.maxH !== calculatedSize.maxH)) {
                            foundGroup.h = calculatedSize.h;
                            foundGroup.maxH = calculatedSize.maxH;
                            foundGroup.minH = calculatedSize.minH;
                        }
                    })
                }
            });
            this.setStateWhenMounted({ layout: allLayouts });
        }
    }

    componentDidMount() {
        const originalLayout = this.getFromLS("layout") || {};
        this.setStateWhenMounted({ layout: JSON.parse(JSON.stringify(originalLayout)) });
        this.subscribe(EventNames.reloadAbsences, this.loadData.bind(this));
    }

    componentDidUpdate() {
        if (this.state.isChanged && this.mounted()) {
            this.setStateWhenMounted({ isChanged: false })
            this.loadWithDebounce();
        }
    }

    static getDerivedStateFromProps(nextProps: AttentionDaysGridLayoutProps, prevState: AttentionDaysGridLayoutState): Partial<AttentionDaysGridLayoutState> {
        const nextDate = new Date(nextProps.date);
        const currentDate = new Date(prevState.currentDate);
        if (currentDate && nextDate.toLocaleDateString() !== currentDate.toLocaleDateString()) {
            return { currentDate: nextDate, isChanged: true };
        }
        return prevState;
    }

    render() {
        const groupedEmployee = this.state.groupedEmployees;

        const bhvMode = this.props.showPresentAndAbsencesInGroups;
        const rowHeight = this.props.showPresentAndAbsencesInGroups ? 12.5 : 15;
        if (!groupedEmployee) return <div></div>;
        const gridItems = groupedEmployee.map((g, i) =>
            <div key={g.id} data-grid={this.getSize(g.employees, g, i, groupedEmployee.length, bhvMode)}>
                <div className="group">
                    <div className="group-header">{g.name}
                        <div className="total-available">Aanwezig: {g.available} / {g.total}</div>
                    </div>
                    <AbsenceDayTable group={g} smallRows={bhvMode} onLoading={() => 0} date={this.state.currentDate} />
                </div>
            </div>)
        const rooms = this.state.rooms;
        let roomItems = <Fragment></Fragment>;
        if (rooms) {
            roomItems = <div key={"all-rooms-group"} data-grid={{ i: "all-rooms-group-index", x: 40, y: 40, w: 4, h: 5 }} className="room-grid-item">
                <div className="group">
                    <div className="group-header">Kamers</div>
                    <div className="room-group-body">
                        {rooms.map((r, i) => {
                            return <div key={i}>
                                <div key={i} className="room-header">{r.name}</div>
                                {r.employees.map((e, i) => <div key={i} className="room-employee">{e.name} {dateFormat(new Date(e.start), "HH:mm")}-{dateFormat(new Date(e.end), "HH:mm")}</div>)}
                            </div>
                        })}
                    </div>
                </div>
            </div>
        }
        return (
            <ResponsiveGridLayout className="layout" rowHeight={rowHeight} isResizable={!bhvMode} isDraggable={!bhvMode} onLayoutChange={this.onLayoutChange.bind(this)} autoSize={true}
                margin={[10, 0]}
                draggableHandle=".group-header"
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                layouts={this.state.layout} >
                {gridItems}
                {roomItems}
            </ResponsiveGridLayout >
        )
    }
    getSize(calenders: ICalendar[], group: IEmployeeGroupAbsences, index: number, groupCount: number, bhvMode: boolean) {
        let height = bhvMode ? 2 : 3;
        //const eventAmount = flatten().length;

        let totalCalenders = height + (group.employees.length * 2);


        const allLengthsAbove2 = calenders.map(x => x.calendarEvents.length).filter(x => x > 2);
        allLengthsAbove2.forEach(x => {
            totalCalenders += (x - 2);
        })

        height = Math.ceil(totalCalenders);
        return {
            i: group.id,
            x: (groupCount <= 2) ? (index % 2) * 6 : (index % 3) * 4,
            y: 0,
            w: (groupCount <= 2) ? 6 : 4,
            h: height,
            minH: height,
            maxH: height,
            minW: 4,
            maxW: 12
        };
    }

    onLayoutChange(currentLayout: Layout[], allLayouts: Layouts) {
        /*eslint no-console: 0*/
        this.saveToLS("layout", allLayouts);
        this.setStateWhenMounted({ layout: allLayouts });
    }

    getFromLS(key: any) {
        let ls: any = {};
        if (window.localStorage) {
            try {
                ls = JSON.parse(window.localStorage.getItem(localstorageKey) || "") || {};
            } catch (e) {
                /*Ignore*/
            }
        }
        return ls[key];
    }

    saveToLS(key: any, value: any) {
        if (window.localStorage) {
            window.localStorage.setItem(
                localstorageKey,
                JSON.stringify({
                    [key]: value
                })
            );
        }
    }
}
